import React, { useState, useEffect } from 'react';
import { useCurrentAccount, useSignPersonalMessage, ConnectButton } from '@mysten/dapp-kit';
import bs58 from 'bs58';
import { useToast } from '../contexts/ToastContext';

const AccountLinkingModal = ({ isOpen, onClose, currentUser, onUserUpdate }) => {
  const [step, setStep] = useState('overview'); // 'overview', 'connect', 'username', 'manage'
  const [username, setUsername] = useState('');
  const [selectedChain, setSelectedChain] = useState('solana');
  const [linkedWallets, setLinkedWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phantom, setPhantom] = useState(null);
  const suiAccount = useCurrentAccount();
  const { mutateAsync: suiSignPersonalMessage } = useSignPersonalMessage();
  const { success: showSuccessToast } = useToast();
  const [availableWallets, setAvailableWallets] = useState([]);
  const [walletStatuses, setWalletStatuses] = useState(new Map());
  const [showSuiConnectPrompt, setShowSuiConnectPrompt] = useState(false);
  const [showSolanaConnectPrompt, setShowSolanaConnectPrompt] = useState(false);
  const [lastSuiAddress, setLastSuiAddress] = useState(null);

  const supportedChains = [
    { id: 'solana', name: 'Solana', icon: '🟣', color: '#9945FF' },
    { id: 'sui', name: 'Sui', icon: '🟢', color: '#4ED4F9' }
  ];

  useEffect(() => {
    if (isOpen) {
      // Check if Phantom is available
      if (window.phantom) {
        setPhantom(window.phantom);
      }

      // If user exists, go to manage step
      if (currentUser) {
        setStep('manage');
        loadLinkedWallets();
        // Refresh available wallets for the manage screen
        detectAvailableWallets();
      } else {
        setStep('overview');
        // Detect wallets and potentially auto-login
        detectAvailableWallets();
      }
    }
  }, [isOpen, currentUser]);

  // Listen for wallet changes
  useEffect(() => {
    const handleAccountsChanged = () => {
      console.log('🔄 Wallet accounts changed, refreshing...');
      // Only refresh wallet detection, don't auto-login on account changes
      if (!window.phantom) {
        setAvailableWallets([]);
        return;
      }

      const wallets = [];

      try {
        // Check Solana
        if (window.phantom.solana?.publicKey) {
          wallets.push({
            chain: 'solana',
            address: window.phantom.solana.publicKey.toString(),
            name: 'Solana',
            icon: '🟣',
            color: '#9945FF'
          });
        }
      } catch (error) {
        console.error('Error detecting wallets:', error);
      }

      setAvailableWallets(wallets);
      checkAllWalletStatuses(wallets);
    };

    // Listen for Solana account changes
    if (window.phantom?.solana) {
      window.phantom.solana.on('accountChanged', handleAccountsChanged);
    }

    return () => {
      // Cleanup listeners
      if (window.phantom?.solana) {
        window.phantom.solana.removeListener('accountChanged', handleAccountsChanged);
      }
    };
  }, []);

  // Watch for Solana wallet connection and auto-trigger linking
  useEffect(() => {
    const linkSolanaWallet = async () => {
      // Only trigger if:
      // 1. User clicked "Add Solana" (showSolanaConnectPrompt is true)
      // 2. There's a Solana wallet connected
      // 3. The address has changed from the last one we saw
      if (showSolanaConnectPrompt && phantom?.solana?.publicKey) {
        const currentAddress = phantom.solana.publicKey.toString();
        console.log('✅ Solana wallet connected, auto-linking...', currentAddress);
        setShowSolanaConnectPrompt(false);
        setLoading(true);
        
        try {
          // Link Solana wallet using the same endpoint as other wallets
          // Get JWT token from localStorage
          const token = localStorage.getItem('jwt_token');
          if (!token) {
            throw new Error('Authentication required - please log in again');
          }

          const linkResponse = await fetch('/api/user/link-wallet', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              wallet_address: currentAddress,
              chain: 'solana'
            })
          });

          const linkData = await linkResponse.json();
          console.log('🔍 Solana Link response:', linkData);
          
          if (!linkData.success) {
            throw new Error(linkData.error || 'Failed to link Solana wallet');
          }
          
          console.log('✅ Solana wallet linked successfully!', linkData);
          
          // Wallet is now linked - refresh the list
          loadLinkedWallets();
          detectAvailableWallets();
        } catch (error) {
          console.error('❌ Error auto-linking Solana wallet:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    };
    
    linkSolanaWallet();
  }, [phantom?.solana?.publicKey, showSolanaConnectPrompt, currentUser?.id]);

  // Watch for Sui account connection and auto-trigger linking
  useEffect(() => {
    const linkSuiWallet = async () => {
      // Only trigger if:
      // 1. User clicked "Add Sui" (showSuiConnectPrompt is true)
      // 2. There's a Sui account connected
      // 3. The address has changed from the last one we saw
      if (showSuiConnectPrompt && suiAccount?.address && suiAccount.address !== lastSuiAddress) {
        console.log('✅ Sui wallet changed, auto-linking...', suiAccount.address);
        setLastSuiAddress(suiAccount.address);
        setShowSuiConnectPrompt(false);
        setLoading(true);
        
        try {
          // Link SUI wallet using the same endpoint as other wallets
          // Get JWT token from localStorage
          const token = localStorage.getItem('jwt_token');
          if (!token) {
            throw new Error('Authentication required - please log in again');
          }

          const linkResponse = await fetch('/api/user/link-wallet', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              wallet_address: suiAccount.address,
              chain: 'sui'
            })
          });

          const linkData = await linkResponse.json();
          console.log('🔍 SUI Link response:', linkData);
          
          if (!linkData.success) {
            throw new Error(linkData.error || 'Failed to link SUI wallet');
          }
          
          console.log('✅ Sui wallet linked successfully!', linkData);
          
          // Wallet is now linked - refresh the list
          loadLinkedWallets();
          detectAvailableWallets();
        } catch (error) {
          console.error('❌ Error auto-linking Sui wallet:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    };
    
    linkSuiWallet();
  }, [suiAccount?.address, showSuiConnectPrompt, lastSuiAddress, currentUser?.id]);

  // Detect available wallet addresses from Phantom
  const detectAvailableWallets = async () => {
    if (!window.phantom) {
      setAvailableWallets([]);
      return;
    }

    const wallets = [];

    try {
      // Check Solana
      if (window.phantom.solana?.publicKey) {
        wallets.push({
          chain: 'solana',
          address: window.phantom.solana.publicKey.toString(),
          name: 'Solana',
          icon: '🟣',
          color: '#9945FF'
        });
      }


      // Sui wallets are handled via Dapp Kit, not Phantom detection
    } catch (error) {
      console.error('Error detecting wallets:', error);
    }

    setAvailableWallets(wallets);
    checkAllWalletStatuses(wallets);

    // Auto-login if any wallet is already linked to an account
    if (!currentUser && wallets.length > 0) {
      await autoLoginWithLinkedWallet(wallets);
    }
  };

  // Auto-login if any detected wallet is already linked to an account
  const autoLoginWithLinkedWallet = async (wallets) => {
    for (const wallet of wallets) {
      try {
        console.log(`🔐 Checking if ${wallet.chain} wallet can auto-login...`);
        const encodedAddress = encodeURIComponent(wallet.address);
        const response = await fetch(`/api/user-lookup/by-wallet/${wallet.chain}/${encodedAddress}`);
        const data = await response.json();

        if (data.success && data.user) {
          console.log(`✅ Auto-logging in with ${wallet.chain} wallet:`, data.user);
          onUserUpdate(data.user);
          setStep('manage');
          loadLinkedWallets();
          return; // Stop after first successful auto-login
        }
      } catch (error) {
        console.error(`❌ Error checking ${wallet.chain} wallet for auto-login:`, error);
      }
    }
  };

  // Check wallet status (linked or not)
  const checkWalletStatus = async (chain, address) => {
    try {
      console.log(`🔍 Checking wallet status for ${chain}: ${address}`);
      const encodedAddress = encodeURIComponent(address);
      const response = await fetch(`/api/user-lookup/by-wallet/${chain}/${encodedAddress}`);
      const data = await response.json();
      console.log(`📋 Wallet status result:`, data);
      return data.success ? 'linked' : 'available';
    } catch (error) {
      console.error('Error checking wallet status:', error);
      return 'unknown';
    }
  };

  // Check status of all available wallets
  const checkAllWalletStatuses = async (wallets) => {
    const statusMap = new Map();
    const promises = wallets.map(async (wallet) => {
      const status = await checkWalletStatus(wallet.chain, wallet.address);
      statusMap.set(`${wallet.chain}-${wallet.address}`, status);
    });
    await Promise.all(promises);
    setWalletStatuses(statusMap);
  };

  const loadLinkedWallets = async () => {
    if (!currentUser?.id) return;
    
    try {
      const response = await fetch(`/api/user-lookup/${encodeURIComponent(currentUser.id)}/wallets`);
      const data = await response.json();
      if (data.success) {
        setLinkedWallets(data.wallets);
      }
    } catch (err) {
      console.error('Failed to load wallets:', err);
    }
  };

  const connectWallet = async (chain) => {
    if (!phantom) {
      setError('Phantom wallet not found. Please install Phantom.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let address;
      
      // Connect to the appropriate chain
      switch (chain) {
        case 'solana':
          if (!phantom.solana?.isConnected) {
            await phantom.solana.connect();
          }
          address = phantom.solana.publicKey?.toString();
          break;
        case 'sui':
          throw new Error('Sui wallets must be added after creating an account. Please create an account with Solana first.');
        default:
          throw new Error(`Unsupported chain: ${chain}`);
      }

      if (!address) {
        throw new Error('Failed to get wallet address');
      }

      // Check if wallet is already linked to a user
      console.log(`🔗 Checking if ${chain} wallet ${address} is already linked...`);
      const encodedAddress = encodeURIComponent(address);
      const userResponse = await fetch(`/api/user-lookup/by-wallet/${chain}/${encodedAddress}`);
      const userData = await userResponse.json();
      console.log('👤 User lookup result:', userData);

      if (userData.success) {
        // Wallet is already linked to a user
        console.log('✅ Found existing user:', userData.user);
        console.log('🔄 Calling onUserUpdate with:', userData.user);
        onUserUpdate(userData.user);
        console.log('📝 Setting step to manage');
        setStep('manage');
        console.log('🔄 Loading linked wallets');
        loadLinkedWallets();
      } else {
        // New wallet - proceed to username creation
        console.log('🆕 New wallet detected, proceeding to username creation');
        setSelectedChain(chain);
        setStep('username');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async () => {
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get wallet address for selected chain
      let address;
      switch (selectedChain) {
        case 'solana':
          address = phantom.solana.publicKey?.toString();
          break;
        case 'sui':
          throw new Error('Sui wallets must be added after creating an account. Please create an account with Solana first.');
        default:
          throw new Error(`Unsupported chain: ${selectedChain}`);
      }

      // Get nonce
      const nonceResponse = await fetch('/api/auth/nonce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          purpose: 'login', 
          chain: selectedChain 
        })
      });
      const nonceData = await nonceResponse.json();
      
      if (!nonceData.success) {
        throw new Error(nonceData.error || 'Failed to get nonce');
      }

      // Build message
      const message = buildMessage({
        action: 'login',
        nonce: nonceData.nonce,
        domain: window.location.host,
        expires: nonceData.expiresAt,
        chain: selectedChain
      });

      // Sign message
      const signature = await signMessage(selectedChain, message);

      // Verify and create account
      const verifyResponse = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chain: selectedChain,
          address: address,
          signature: signature,
          nonce: nonceData.nonce,
          username: username.trim(),
          action: 'login'
        })
      });

      const verifyData = await verifyResponse.json();
      
      if (verifyData.success) {
        // Account created successfully
        onUserUpdate({ id: verifyData.userId, username: username.trim() });
        setStep('manage');
        loadLinkedWallets();
      } else {
        throw new Error(verifyData.error || 'Failed to create account');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const linkAdditionalWallet = async (chain) => {
    console.log(`🔗 linkAdditionalWallet called with chain: ${chain}`);
    if (!currentUser) {
      console.log('❌ No current user, returning');
      return;
    }

    // Hide the opposite connection prompt when starting a new wallet linking process
    if (chain === 'solana') {
      setShowSuiConnectPrompt(false);
    } else if (chain === 'sui') {
      setShowSolanaConnectPrompt(false);
    }

    setLoading(true);
    setError('');

    try {
      console.log(`🔄 Starting link process for ${chain}`);
      // Get wallet address
      let address;
      switch (chain) {
        case 'solana':
          if (!phantom) {
            setError('Phantom wallet not found. Please install Phantom.');
            return;
          }
          if (!phantom.solana?.isConnected) {
            // Show the Solana connect prompt instead of trying to connect directly
            setShowSolanaConnectPrompt(true);
            throw new Error('solana-wallet-selection');
          }
          address = phantom.solana.publicKey?.toString();
          break;
        case 'sui':
          // Always show the wallet selector to allow users to choose/switch wallets
          console.log('🔌 Showing Sui wallet selector...');
          setShowSuiConnectPrompt(true);
          throw new Error('sui-wallet-selection');
          // This will be caught and show the ConnectButton UI
          // After connection, the auto-link useEffect will trigger
        default:
          throw new Error(`Unsupported chain: ${chain}`);
      }

      // For account linking, we don't need signature verification
      // The wallet connection itself is sufficient proof of ownership
      console.log('🔗 Proceeding with wallet linking for', chain);

      // Link wallet using the new JWT-based system
      // For account linking, we need to add the wallet to the existing user's account
      // This should be done through a dedicated wallet linking endpoint
      console.log('🔗 Linking wallet to existing user account...');
      
      // Use the new link-wallet endpoint to add wallet to existing user
      // Get JWT token from localStorage
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        throw new Error('Authentication required - please log in again');
      }

      const linkResponse = await fetch('/api/user/link-wallet', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          wallet_address: address,
          chain: chain
        })
      });

      const linkData = await linkResponse.json();
      console.log('🔍 Link response:', linkData);
      
      if (!linkData.success) {
        throw new Error(linkData.error || 'Failed to link wallet');
      }

      // Wallet linked successfully
      loadLinkedWallets();
      detectAvailableWallets(); // Refresh available wallets
    } catch (err) {
      // Special case: Wallet selection is not an error
      if (err.message === 'sui-wallet-selection') {
        console.log('💡 Showing Sui wallet selector to user');
        setLoading(false);
        return; // Don't show error, just show the connect prompt
      }
      if (err.message === 'solana-wallet-selection') {
        console.log('💡 Showing Solana wallet selector to user');
        setLoading(false);
        return; // Don't show error, just show the connect prompt
      }
      
      console.error(`❌ Error linking ${chain} wallet:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildMessage = ({ action, nonce, user, domain, expires, chain }) => {
    return [
      'SoulWars Account Linking',
      `Action: ${action}`,
      `Nonce: ${nonce}`,
      `User: ${user || 'anonymous'}`,
      `Domain: ${domain}`,
      `Expires: ${expires}`,
      `Chain: ${chain}`,
    ].join('\n');
  };

  const signMessage = async (chain, message) => {
    switch (chain) {
      case 'solana':
        const encodedMessage = new TextEncoder().encode(message);
        const solanaResponse = await phantom.solana.signMessage(encodedMessage, 'utf8');
        return bs58.encode(solanaResponse.signature);
      default:
        throw new Error(`Unsupported chain: ${chain}`);
    }
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast('Wallet address copied to clipboard!');
      console.log('Address copied to clipboard:', text);
    } catch (err) {
      console.error('Failed to copy address:', err);
      // Could add error toast here if needed
    }
  };

  const getChainInfo = (chainId) => {
    return supportedChains.find(c => c.id === chainId) || { name: chainId, icon: '🔗', color: '#666' };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            {step === 'overview' && '🔗 Account Linking'}
            {step === 'connect' && '🔌 Connect Wallet'}
            {step === 'username' && '👤 Create Account'}
            {step === 'manage' && '⚙️ Account Settings'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl leading-none"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {step === 'overview' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🎮</div>
              <h3 className="text-xl font-semibold text-white mb-2">Welcome to SoulWars!</h3>
              <p className="text-gray-300 mb-6">
                Link your wallets to create a unified gaming identity and track your progress across all chains.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="font-semibold text-white mb-2">🎯 Benefits</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Unified username across chains</li>
                  <li>• Track all your game stats</li>
                  <li>• Link multiple wallets</li>
                  <li>• Secure verification</li>
                </ul>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="font-semibold text-white mb-2">🔒 Security</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Message signing verification</li>
                  <li>• No private keys stored</li>
                  <li>• One-time nonces</li>
                  <li>• You control your wallets</li>
                </ul>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">Choose a Chain to Connect:</h4>
              <p className="text-sm text-gray-300 mb-4">
                Link your wallet to create a SoulWars account or sign in to an existing one.
              </p>
              <div className="space-y-2">
                {supportedChains.map((chain) => {
                  const availableWallet = availableWallets.find(w => w.chain === chain.id);
                  const status = availableWallet ? walletStatuses.get(`${chain.id}-${availableWallet.address}`) : null;
                  const isLinkedToAnyUser = status === 'linked';

                  // If user is logged in, check if this specific wallet address is linked to their account
                  const isLinkedToCurrentUser = currentUser && availableWallet
                    ? linkedWallets.some(lw => lw.chain === chain.id && lw.address === availableWallet.address)
                    : false;

                  const isLinked = currentUser ? isLinkedToCurrentUser : isLinkedToAnyUser;

                  return (
                    <div
                      key={chain.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isLinked
                          ? 'bg-green-900 border-green-700'
                          : availableWallet
                            ? 'bg-gray-800 border-gray-600'
                            : 'bg-gray-800 border-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{chain.icon}</span>
                        <div>
                          <div className="font-medium text-white">{chain.name}</div>
                          {availableWallet && (
                            <div className="text-xs text-gray-400 font-mono">
                              {formatAddress(availableWallet.address)}
                            </div>
                          )}
                          {!availableWallet && (
                            <div className="text-xs text-gray-500">
                              Not connected in Phantom
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {isLinked ? (
                          <span className="text-green-400 text-sm">✓ Already Linked</span>
                        ) : availableWallet ? (
                          <button
                            onClick={() => connectWallet(chain.id)}
                            disabled={loading}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded"
                          >
                            Link Wallet
                          </button>
                        ) : (
                          <button
                            onClick={detectAvailableWallets}
                            disabled={loading}
                            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm rounded"
                          >
                            Check
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <div className="flex space-x-3">
                <button
                  onClick={detectAvailableWallets}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white rounded-lg"
                >
                  🔄 Refresh Wallets
                </button>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Maybe Later
                </button>
                {availableWallets.length > 0 && (
                  <button
                    onClick={() => setStep('connect')}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Choose Wallet
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 'connect' && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Choose a Wallet to Connect</h3>
              <p className="text-gray-300">
                Select which wallet you'd like to use for your SoulWars account
              </p>
            </div>

            <div className="space-y-3">
              {availableWallets.map((wallet, index) => {
                const status = walletStatuses.get(`${wallet.chain}-${wallet.address}`);
                const isLinked = status === 'linked';
                
                return (
                  <button
                    key={`${wallet.chain}-${index}`}
                    onClick={() => connectWallet(wallet.chain)}
                    disabled={loading || isLinked}
                    className={`w-full flex items-center justify-between p-4 rounded-lg text-white disabled:opacity-50 ${
                      isLinked 
                        ? 'bg-green-900 border border-green-700' 
                        : 'bg-gray-800 hover:bg-gray-700 border border-gray-600'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-3xl mr-4">{wallet.icon}</span>
                      <div className="text-left">
                        <div className="font-medium text-lg">{wallet.name}</div>
                        <div className="text-sm text-gray-400 font-mono">
                          {formatAddress(wallet.address)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {isLinked ? (
                        <span className="text-green-400">✓ Already Linked</span>
                      ) : (
                        <span className="text-gray-400">→</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('overview')}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {step === 'username' && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Create Your Username</h3>
              <p className="text-gray-300">
                Choose a unique username for your SoulWars account
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 text-lg"
                maxLength={50}
                autoFocus
              />
              <div className="text-xs text-gray-400 mt-1">
                This will be your display name in games and leaderboards
              </div>
            </div>

            <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-100 mb-2">🔐 Security Notice</h4>
              <p className="text-sm text-blue-200">
                You'll be asked to sign a message to verify wallet ownership. This doesn't give us access to your funds.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('connect')}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Back
              </button>
              <button
                onClick={createAccount}
                disabled={loading || !username.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg"
              >
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </div>
        )}

        {step === 'manage' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl mb-2">👤</div>
              <h3 className="text-xl font-semibold text-white mb-2">{currentUser?.username}</h3>
              <p className="text-gray-400 text-sm">User ID: {currentUser?.id}</p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-3">Linked Wallets</h4>
              <div className="space-y-2">
                {linkedWallets.map(wallet => {
                  const chainInfo = getChainInfo(wallet.chain);
                  return (
                    <div key={wallet.id} className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{chainInfo.icon}</span>
                        <div>
                          <div className="text-white font-medium">{chainInfo.name}</div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm font-mono">{formatAddress(wallet.address)}</span>
                            <button
                              onClick={() => copyToClipboard(wallet.address)}
                              className="text-gray-500 hover:text-gray-300 transition-colors"
                              title="Copy full address"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                      <span className="text-green-400 text-sm">✓ Linked</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {showSuiConnectPrompt && (
              <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 mb-4">
                <div className="text-center">
                  <p className="text-white mb-3">
                    {suiAccount?.address 
                      ? 'Click to switch to a different Sui wallet or connect another one:'
                      : 'To add a Sui wallet, first connect it:'}
                  </p>
                  <ConnectButton connectText={suiAccount?.address ? "Switch/Connect Sui Wallet" : "Connect Sui Wallet"} />
                  {suiAccount?.address && (
                    <div className="mt-3 text-sm text-gray-400">
                      Currently connected: <span className="font-mono">{formatAddress(suiAccount.address)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showSolanaConnectPrompt && (
              <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4 mb-4">
                <div className="text-center">
                  <p className="text-white mb-3">
                    {phantom?.solana?.publicKey
                      ? 'Click to switch to a different Solana wallet or connect another one:'
                      : 'To add a Solana wallet, first connect it:'}
                  </p>
                  <button
                    onClick={async () => {
                      if (!phantom) {
                        setError('Phantom wallet not found. Please install Phantom.');
                        return;
                      }
                      try {
                        if (!phantom.solana?.isConnected) {
                          await phantom.solana.connect();
                        }
                        // After connection, the wallet detection will update and we can link it
                        detectAvailableWallets();
                        setShowSolanaConnectPrompt(false);
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                  >
                    {phantom?.solana?.publicKey ? "Switch/Connect Solana Wallet" : "Connect Solana Wallet"}
                  </button>
                  {phantom?.solana?.publicKey && (
                    <div className="mt-3 text-sm text-gray-400">
                      Currently connected: <span className="font-mono">{formatAddress(phantom.solana.publicKey.toString())}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-semibold text-white mb-3">Add More Wallets</h4>
              <div className="bg-gray-800/50 rounded-lg p-3 mb-3 text-sm text-gray-300">
                💡 <strong>Tip:</strong> You can link multiple wallets of the same type!
                <div className="mt-1 text-xs text-gray-400">
                  • <strong>Solana:</strong> Switch wallets in Phantom, then click "Add Solana"
                  <br />
                  • <strong>Sui:</strong> Click "Add Sui" to select from any connected Sui wallet
                </div>
              </div>
              <div className="space-y-2">
                {supportedChains.map((chain) => {
                  const availableWallet = availableWallets.find(w => w.chain === chain.id);
                  
                  // Check if THIS specific wallet address is already linked (not just any wallet of this chain type)
                  const isThisWalletLinked = availableWallet && linkedWallets.some(
                    lw => lw.chain === chain.id && lw.address.toLowerCase() === availableWallet.address.toLowerCase()
                  );

                  // For Sui, always allow adding (wallet selector will handle multiple wallets)
                  const canAddSui = chain.id === 'sui';
                  
                  // For Solana, always allow adding (connection prompt will handle wallet connection)
                  const canAddSolana = chain.id === 'solana';

                  const canAddWallet = canAddSolana || canAddSui;

                  return (
                    <button
                      key={chain.id}
                      onClick={() => linkAdditionalWallet(chain.id)}
                      disabled={loading || (availableWallet && isThisWalletLinked)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-white disabled:opacity-50 ${
                        isThisWalletLinked
                          ? 'bg-green-900 border border-green-700 cursor-not-allowed'
                          : canAddWallet
                            ? 'bg-gray-800 hover:bg-gray-700'
                            : 'bg-gray-700 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{chain.icon}</span>
                        <div className="text-left">
                          <div className="font-medium">
                            {isThisWalletLinked ? '✓' : canAddWallet ? '+' : '⚠️'} Add {chain.name}
                          </div>
                          {availableWallet && (
                            <div className="text-gray-400 text-sm font-mono">
                              {formatAddress(availableWallet.address)}
                            </div>
                          )}
                          {isThisWalletLinked && (
                            <div className="text-green-400 text-sm">
                              This wallet is already linked
                            </div>
                          )}
                          {!availableWallet && chain.id === 'solana' && (
                            <div className="text-blue-400 text-sm">
                              Click to connect another Solana wallet
                            </div>
                          )}
                          {chain.id === 'sui' && !isThisWalletLinked && (
                            <div className="text-blue-400 text-sm">
                              Click to connect another Sui wallet
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {isThisWalletLinked ? (
                          <span className="text-green-400">Already Linked</span>
                        ) : canAddWallet ? (
                          <span className="text-gray-400">→</span>
                        ) : (
                          <span className="text-gray-600">Unavailable</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  detectAvailableWallets();
                  loadLinkedWallets();
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white rounded-lg"
              >
                🔄 Refresh
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountLinkingModal;
